/**
 * Mantenimiento → Categorías (`/mantenimiento/categorias`).
 *
 * Puerto de `Maintenance::categories/store_category/update_category/
 * toggle_category_status` + `views/maintenance/categories/index.php`: el catálogo
 * que agrupa los activos que se mantienen, con su color y su estado.
 *
 * La tabla del panel anterior tenía las mismas columnas (color, nombre,
 * descripción, activos, estado, acciones) y un buscador; aquí se añade el filtro
 * de estado y se conserva todo lo demás, incluida la paginación desde la API
 * (la pantalla pide una página amplia y filtra en el cliente).
 */

import {
  ArrowLeft,
  Ban,
  Boxes,
  Palette,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  ShieldAlert,
  Wrench,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatNumber } from '@/lib/utils';

import {
  useActualizarCategoria,
  useCambiarEstadoCategoria,
  useCategorias,
  useCrearCategoria,
} from '../categorias.api';
import type { CategoriaMantenimiento, CategoriaPayload } from '../categorias.types';

/** Filtro de estado del listado. */
type FiltroEstado = 'todas' | 'activas' | 'inactivas';

/** Paginación amplia: el catálogo es corto y se filtra en el cliente. */
const POR_PAGINA = 200;

const MAX_NOMBRE = 100;
const RE_COLOR = /^#[0-9A-Fa-f]{6}$/;

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** Muestra el color de la categoría. */
function MuestraColor({ color, className }: { color: string; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn('inline-block size-6 shrink-0 rounded-md border border-ink-200', className)}
      style={{ backgroundColor: color }}
    />
  );
}

/**
 * Alta y edición de una categoría.
 *
 * `categoria` nulo ⇒ alta. El color se puede elegir con la paleta o escribirlo a
 * mano, como en el panel anterior (que sincronizaba los dos campos).
 */
function CategoriaDialogo({
  categoria,
  guardando,
  error,
  onGuardar,
  onCerrar,
}: {
  categoria: CategoriaMantenimiento | null;
  guardando: boolean;
  error: string | null;
  onGuardar: (payload: CategoriaPayload) => void;
  onCerrar: () => void;
}) {
  const [nombre, setNombre] = useState(categoria?.nombre ?? '');
  const [descripcion, setDescripcion] = useState(categoria?.descripcion ?? '');
  const [color, setColor] = useState(categoria?.color ?? '#007bff');

  const nombreLimpio = nombre.trim();
  const errorNombre =
    nombreLimpio.length === 0
      ? 'El nombre es obligatorio.'
      : nombreLimpio.length > MAX_NOMBRE
        ? `Máximo ${MAX_NOMBRE} caracteres.`
        : null;
  const errorColor = RE_COLOR.test(color.trim())
    ? null
    : 'El color debe tener el formato #RRGGBB (ej. #007bff).';

  const valido = !errorNombre && !errorColor;

  return (
    <Dialog
      open
      onClose={onCerrar}
      title={categoria ? 'Editar categoría' : 'Nueva categoría'}
      description={
        categoria
          ? 'Los cambios se aplican a la categoría y a la etiqueta con la que se pinta en el panel.'
          : 'Agrupa los activos que comparten el mismo tipo de mantenimiento.'
      }
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            disabled={!valido}
            onClick={() =>
              onGuardar({ nombre: nombreLimpio, descripcion: descripcion.trim(), color: color.trim() })
            }
          >
            {categoria ? 'Guardar cambios' : 'Crear categoría'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <Alert variant="error" title="No se pudo guardar">
            {error}
          </Alert>
        ) : null}

        <Input
          label="Nombre"
          value={nombre}
          maxLength={MAX_NOMBRE}
          autoFocus
          placeholder="Ej. Dispensadores, Servidores"
          error={nombreLimpio.length > 0 ? (errorNombre ?? undefined) : undefined}
          hint={nombreLimpio.length === 0 ? 'Hasta 100 caracteres, sin repetir otra categoría' : undefined}
          onChange={(evento) => setNombre(evento.target.value)}
        />

        <div className="flex flex-col gap-1.5">
          <label htmlFor="categoria-descripcion" className="text-sm font-medium text-ink-700">
            Descripción
          </label>
          <textarea
            id="categoria-descripcion"
            rows={3}
            value={descripcion}
            placeholder="Detalles adicionales"
            className="w-full rounded-control border border-ink-200 bg-white px-4 py-3 text-sm text-ink-900 placeholder:text-ink-400 outline-none transition-all duration-200 focus:border-brand-400 focus:ring-4 focus:ring-brand-100"
            onChange={(evento) => setDescripcion(evento.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="categoria-color" className="text-sm font-medium text-ink-700">
            Color
          </label>
          <div className="flex items-start gap-2">
            <input
              id="categoria-color"
              type="color"
              value={RE_COLOR.test(color) ? color : '#007bff'}
              className="h-12 w-16 shrink-0 cursor-pointer rounded-control border border-ink-200 bg-white p-1"
              onChange={(evento) => setColor(evento.target.value)}
            />
            <Input
              containerClassName="flex-1"
              value={color}
              maxLength={7}
              placeholder="#007bff"
              className="font-mono uppercase"
              leftIcon={<Palette className="size-4" aria-hidden="true" />}
              error={color.trim().length > 0 ? (errorColor ?? undefined) : undefined}
              hint={color.trim().length === 0 ? 'Formato #RRGGBB' : undefined}
              onChange={(evento) => setColor(evento.target.value)}
            />
          </div>
        </div>
      </div>
    </Dialog>
  );
}

export function CategoriasPage() {
  useDocumentTitle('Mantenimiento · Categorías');

  const [busqueda, setBusqueda] = useState('');
  const [estado, setEstado] = useState<FiltroEstado>('todas');
  const [dialogo, setDialogo] = useState<{ categoria: CategoriaMantenimiento | null } | null>(null);
  const [aCambiar, setACambiar] = useState<CategoriaMantenimiento | null>(null);
  const [errorDialogo, setErrorDialogo] = useState<string | null>(null);
  const [errorConfirmacion, setErrorConfirmacion] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const categorias = useCategorias({ perPage: POR_PAGINA });
  const crear = useCrearCategoria();
  const actualizar = useActualizarCategoria();
  const cambiarEstado = useCambiarEstadoCategoria();

  const sinPermiso = categorias.error instanceof ApiError && categorias.error.status === 403;
  const lista = categorias.data?.categorias ?? [];
  const total = categorias.data?.total ?? 0;

  const termino = busqueda.trim().toLowerCase();
  const filtradas = lista.filter((categoria) => {
    if (estado === 'activas' && !categoria.activa) return false;
    if (estado === 'inactivas' && categoria.activa) return false;

    if (!termino) return true;

    return (
      categoria.nombre.toLowerCase().includes(termino) ||
      categoria.descripcion.toLowerCase().includes(termino)
    );
  });

  const activas = lista.filter((categoria) => categoria.activa).length;
  const guardando = crear.isPending || actualizar.isPending;

  /** Guarda el alta o la edición según el diálogo abierto. */
  function guardar(payload: CategoriaPayload) {
    setErrorDialogo(null);

    const comun = {
      onSuccess: (respuesta: { mensaje: string }) => {
        setAviso(respuesta.mensaje);
        setDialogo(null);
      },
      onError: (error: unknown) => setErrorDialogo(mensajeDe(error, 'La API no respondió.')),
    };

    if (dialogo?.categoria) {
      actualizar.mutate({ id: dialogo.categoria.id, payload }, comun);
      return;
    }

    crear.mutate(payload, comun);
  }

  /** Activa o desactiva la categoría pendiente de confirmación. */
  function confirmarEstado() {
    if (!aCambiar) return;

    setErrorConfirmacion(null);

    cambiarEstado.mutate(
      { id: aCambiar.id, activa: !aCambiar.activa },
      {
        onSuccess: (respuesta) => {
          setAviso(respuesta.mensaje);
          setACambiar(null);
        },
        onError: (error) => setErrorConfirmacion(mensajeDe(error, 'La API no respondió.')),
      },
    );
  }

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/inicio/mantenimiento"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Mantenimiento
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <Wrench className="size-6 text-brand-500" aria-hidden="true" />
          Categorías de mantenimiento
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Tipos de mantenimiento con los que se clasifican los activos. El color es la etiqueta con
          la que se pintan en el panel.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {categorias.data ? (
          <Badge variant="outline">
            {total} categorías · {activas} activas
          </Badge>
        ) : null}
        <Button
          size="sm"
          leftIcon={<Plus className="size-3.5" aria-hidden="true" />}
          disabled={sinPermiso}
          onClick={() => {
            setErrorDialogo(null);
            setDialogo({ categoria: null });
          }}
        >
          Nueva categoría
        </Button>
      </div>
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es del grupo <strong>maintenance</strong>.
          </span>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {cabecera}

      {aviso ? <Alert variant="success">{aviso}</Alert> : null}

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              containerClassName="min-w-56 flex-1"
              leftIcon={<Search className="size-4" aria-hidden="true" />}
              placeholder="Buscar por nombre o descripción…"
              value={busqueda}
              onChange={(evento) => setBusqueda(evento.target.value)}
            />

            <div className="flex items-center gap-1 rounded-control border border-ink-200 bg-white p-1">
              {(['todas', 'activas', 'inactivas'] as const).map((opcion) => (
                <button
                  key={opcion}
                  type="button"
                  className={cn(
                    'rounded-md px-3 py-1.5 text-xs font-semibold capitalize transition-colors',
                    estado === opcion
                      ? 'bg-brand-50 text-brand-700'
                      : 'text-ink-500 hover:bg-ink-50 hover:text-ink-700',
                  )}
                  onClick={() => setEstado(opcion)}
                >
                  {opcion}
                </button>
              ))}
            </div>
          </div>

          {categorias.isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : categorias.error ? (
            <Alert variant="error" title="No se pudieron leer las categorías">
              {mensajeDe(categorias.error, 'La API no respondió.')}
            </Alert>
          ) : filtradas.length === 0 ? (
            <Alert variant="info" title="Sin resultados">
              {termino || estado !== 'todas'
                ? 'Ninguna categoría coincide con el filtro.'
                : 'Todavía no hay categorías registradas.'}
            </Alert>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-ink-100 text-xs font-semibold uppercase tracking-wider text-ink-400">
                    <th className="py-2">Categoría</th>
                    <th className="py-2">Descripción</th>
                    <th className="py-2">Activos</th>
                    <th className="py-2">Estado</th>
                    <th className="py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filtradas.map((categoria) => (
                    <tr key={categoria.id} className="border-b border-ink-50 last:border-0">
                      <td className="py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <MuestraColor color={categoria.color} />
                          <span className="min-w-0">
                            <span className="block truncate font-semibold text-ink-900">
                              {categoria.nombre}
                            </span>
                            <span className="block font-mono text-[11px] text-ink-400">
                              {categoria.color} · #{categoria.id}
                            </span>
                          </span>
                        </div>
                      </td>
                      <td className="py-3 text-ink-600">
                        {categoria.descripcion || <span className="text-ink-300">—</span>}
                      </td>
                      <td className="py-3">
                        <Badge variant={categoria.activos > 0 ? 'brand' : 'neutral'}>
                          <Boxes className="size-3" aria-hidden="true" />
                          {formatNumber(categoria.activos)}
                        </Badge>
                      </td>
                      <td className="py-3">
                        <Badge variant={categoria.activa ? 'success' : 'neutral'}>
                          {categoria.activa ? 'Activa' : 'Inactiva'}
                        </Badge>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Editar"
                            aria-label={`Editar ${categoria.nombre}`}
                            onClick={() => {
                              setErrorDialogo(null);
                              setDialogo({ categoria });
                            }}
                          >
                            <Pencil className="size-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            title={categoria.activa ? 'Desactivar' : 'Reactivar'}
                            aria-label={
                              categoria.activa
                                ? `Desactivar ${categoria.nombre}`
                                : `Reactivar ${categoria.nombre}`
                            }
                            onClick={() => {
                              setErrorConfirmacion(null);
                              setACambiar(categoria);
                            }}
                          >
                            {categoria.activa ? (
                              <Ban className="size-4 text-rose-500" aria-hidden="true" />
                            ) : (
                              <RotateCcw className="size-4 text-emerald-600" aria-hidden="true" />
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {total > lista.length ? (
            <p className="text-xs text-ink-400">
              Mostrando {lista.length} de {total} categorías. Usa el buscador para acotar.
            </p>
          ) : null}
        </CardContent>
      </Card>

      {dialogo ? (
        <CategoriaDialogo
          categoria={dialogo.categoria}
          guardando={guardando}
          error={errorDialogo}
          onGuardar={guardar}
          onCerrar={() => setDialogo(null)}
        />
      ) : null}

      {aCambiar ? (
        <ConfirmDialog
          open
          tone={aCambiar.activa ? 'danger' : 'primary'}
          title={aCambiar.activa ? 'Desactivar categoría' : 'Reactivar categoría'}
          description={
            aCambiar.activa
              ? 'La categoría deja de ofrecerse al registrar activos, pero se conserva.'
              : 'La categoría vuelve a estar disponible al registrar activos.'
          }
          confirmLabel={aCambiar.activa ? 'Desactivar' : 'Reactivar'}
          loading={cambiarEstado.isPending}
          onClose={() => setACambiar(null)}
          onConfirm={confirmarEstado}
        >
          <div className="flex flex-col gap-3">
            <p className="flex items-center gap-2 text-sm text-ink-600">
              <MuestraColor color={aCambiar.color} />
              <strong className="text-ink-900">{aCambiar.nombre}</strong>
            </p>

            {aCambiar.activa && aCambiar.activos > 0 ? (
              <Alert variant="warning">
                Esta categoría tiene <strong>{formatNumber(aCambiar.activos)} activos</strong>. No se
                borran: sólo dejará de estar disponible para nuevos registros.
              </Alert>
            ) : null}

            {errorConfirmacion ? (
              <Alert variant="error" title="No se pudo cambiar el estado">
                {errorConfirmacion}
              </Alert>
            ) : null}
          </div>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}
